import { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Loader2, Download, Lock, ArrowLeft, ShieldCheck, Sparkles, PenLine, CopyPlus, RefreshCw, History, ChevronDown, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { AppShell } from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { StarRating } from "@/components/StarRating";
import { SkillPreviewPane } from "@/components/SkillPreviewPane";
import { ReviewSection } from "@/components/ReviewSection";
import { DiffText } from "@/components/DiffText";
import { api, downloadSkillZip, apiErr } from "@/lib/api";
import { UseInClaudeApps } from "@/components/UseInClaudeApps";
import { purchaseOrDownload, creditsLabel } from "@/lib/payments";
import { kindOf, kindBadge } from "@/lib/kinds";
import { useAuth } from "@/context/AuthContext";

// Creator-only: set what buyers pay (credits; 0 = free). Server bounds + validates price_cents.
function PriceEditor({ skill, onSaved }) {
  const [credits, setCredits] = useState(String((skill.price_cents ?? 100) / 100));
  const [saving, setSaving] = useState(false);
  const save = async () => {
    const cents = Math.max(0, Math.round((parseFloat(credits) || 0) * 100));
    setSaving(true);
    try {
      await api.patch(`/skills/${skill.id}`, { price_cents: cents });
      toast.success(cents === 0 ? "Now free" : `Buyers now pay ${creditsLabel(cents)}`);
      onSaved();
    } catch (e) {
      toast.error(apiErr(e, "Couldn't update price"));
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="mt-4 border-t border-[color:var(--rb-border)] pt-4">
      <label className="text-xs text-[color:var(--rb-text-faint)]">Price to buyers (credits) — 0 is free</label>
      <div className="mt-1.5 flex gap-2">
        <Input type="number" min="0" max="1000" step="1" value={credits}
          onChange={(e) => setCredits(e.target.value)} data-testid="price-input"
          className="border-[color:var(--rb-border)] bg-[color:var(--rb-code)] text-[color:var(--rb-text)]" />
        <Button onClick={save} disabled={saving} variant="outline" data-testid="save-price-button"
          className="shrink-0 border-[color:var(--rb-border)] text-[color:var(--rb-text)] hover:bg-[color:var(--rb-surface)]">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save price"}
        </Button>
      </div>
    </div>
  );
}

// Content-version list with full per-file diffs (2026-07-14). Only rendered when the content
// itself is unlocked — the API enforces the same gate. `canRestore` adds git-revert-style
// restore (an old snapshot becomes a NEW head version — nothing is deleted).
function VersionHistory({ skillId, canRestore = false, onRestored = null }) {
  const [versions, setVersions] = useState(null);
  const [open, setOpen] = useState(null); // expanded version number
  const [diffs, setDiffs] = useState({}); // version -> {files: [{filename, status, diff}]}
  useEffect(() => {
    api.get(`/skills/${skillId}/versions`)
      .then(({ data }) => setVersions(data.versions))
      .catch(() => setVersions([]));
  }, [skillId]);
  const toggle = async (v) => {
    if (open === v) return setOpen(null);
    setOpen(v);
    if (!diffs[v]) {
      try {
        const { data } = await api.get(`/skills/${skillId}/versions/${v}/diff`);
        setDiffs((d) => ({ ...d, [v]: data }));
      } catch (e) {
        toast.error(apiErr(e, "Couldn't load the diff"));
      }
    }
  };
  const restore = async (v) => {
    if (!window.confirm(`Restore v${v} as the new current version?`)) return;
    try {
      const { data } = await api.post(`/skills/${skillId}/versions/${v}/restore`);
      toast.success(data.message === "No changes to save."
        ? "Already identical to the current version."
        : `Restored v${v} as v${data.content_version}. ${data.message}`);
      setOpen(null);
      setDiffs({});
      const r = await api.get(`/skills/${skillId}/versions`);
      setVersions(r.data.versions);
      if (onRestored) onRestored();
    } catch (e) {
      toast.error(apiErr(e, "Couldn't restore"));
    }
  };
  if (!versions?.length) return null;
  return (
    <div className="rounded-2xl border border-[color:var(--rb-border)] bg-[color:var(--rb-obsidian)] p-5" data-testid="version-history">
      <h2 className="flex items-center gap-2 text-sm font-medium text-[color:var(--rb-text)]">
        <History className="h-4 w-4 text-[color:var(--rb-accent)]" /> Version history
      </h2>
      <div className="mt-3 space-y-1.5">
        {versions.map((v) => (
          <div key={v.version}>
            <button onClick={() => v.version > 1 && toggle(v.version)} disabled={v.version === 1}
              data-testid={`version-row-${v.version}`}
              className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-sm text-[color:var(--rb-text-dim)] enabled:hover:bg-[color:var(--rb-surface)] disabled:cursor-default">
              {v.version > 1 ? (open === v.version ? <ChevronDown className="h-3.5 w-3.5 shrink-0" /> : <ChevronRight className="h-3.5 w-3.5 shrink-0" />) : <span className="w-3.5 shrink-0" />}
              <span className="rb-mono text-[color:var(--rb-text)]">v{v.version}</span>
              {v.current && <Badge className="border-[color:rgba(45,212,191,0.3)] bg-[color:rgba(45,212,191,0.12)] text-[10px] text-[color:var(--rb-accent)]">current</Badge>}
              <span className="text-xs text-[color:var(--rb-text-faint)]">
                {(v.saved_at || "").slice(0, 10)}{v.saved_by ? ` · @${v.saved_by}` : ""}{v.version === 1 ? " · initial version" : ""}
              </span>
            </button>
            {open === v.version && (
              <>
                {canRestore && !v.current && (
                  <button onClick={() => restore(v.version)} data-testid={`restore-version-${v.version}`}
                    className="mb-1 ml-6 flex items-center gap-1 rounded-lg border border-[color:var(--rb-border)] px-2 py-1 text-xs text-[color:var(--rb-text-dim)] hover:border-[color:var(--rb-border-strong)] hover:text-[color:var(--rb-text)]">
                    <RefreshCw className="h-3 w-3" /> Restore this version
                  </button>
                )}
                {diffs[v.version] ? (
                  diffs[v.version].files.length ? diffs[v.version].files.map((f) => (
                    <div key={f.filename} className="mb-2 ml-6">
                      <div className="rb-mono text-xs text-[color:var(--rb-text-faint)]">{f.filename} <span className="text-[color:var(--rb-text-dim)]">({f.status})</span></div>
                      <DiffText text={f.diff} />
                    </div>
                  )) : <p className="ml-6 text-xs text-[color:var(--rb-text-faint)]">No content changes in this version.</p>
                ) : <Loader2 className="ml-6 h-4 w-4 animate-spin text-[color:var(--rb-text-faint)]" />}
              </>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

export default function SkillDetail() {
  const { skillId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { org } = useAuth();
  const [skill, setSkill] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  // Breadcrumb: go back to wherever you actually came from, not always the marketplace.
  const back = location.state?.from === "my-skills"
    ? { to: "/my-skills", label: "My Artifacts" }
    : { to: "/marketplace", label: "Marketplace" };

  const load = async () => {
    try {
      const { data } = await api.get(`/skills/${skillId}`);
      setSkill(data);
    } catch (e) {
      toast.error("Skill not found");
      navigate("/marketplace");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [skillId]);

  // Unique title + description per skill so each page is its own rankable result.
  useEffect(() => {
    if (!skill) return;
    const noun = skill.kind === "agent" ? "Claude Agent" : "Claude Skill";
    document.title = `${skill.slug} — ${noun} | Runebox`;
    const desc = skill.short_description || skill.long_description || `Download ${skill.slug}, a ${noun} built with best practices.`;
    document.querySelector('meta[name="description"]')?.setAttribute("content", desc.slice(0, 160));
  }, [skill]);

  const onBuy = async () => {
    setBusy(true);
    try {
      if (org) {
        // Org members don't juggle zips (2026-07-13): buy it and the plugin installs it.
        await api.post(`/skills/${skill.id}/purchase`);
        toast.success("Purchased — your plugin will sync it on the next session.");
      } else {
        const res = await purchaseOrDownload(skill.id, skill.slug);
        toast.success(res.action === "downloaded" ? "Downloaded" : "Purchased & downloaded");
      }
      load();
    } catch (e) {
      if (e.code === "insufficient_credits") {
        toast.error("Not enough credits — top up your wallet to buy this skill.");
        navigate("/wallet");
      } else {
        const d = e.response?.data?.detail;
        toast.error((d && (d.message || d)) || e.message || "Purchase failed");
      }
    } finally {
      setBusy(false);
    }
  };

  const onDownload = async () => {
    try {
      await downloadSkillZip(skill.id, skill.slug);
      toast.success("Downloaded");
    } catch (e) {
      toast.error(e.message || "Download failed");
    }
  };

  // Refine (creator only): resume/create the builder session for this skill and jump into it.
  const onRefine = async () => {
    try {
      const { data } = await api.post(`/chat/sessions/for-skill/${skill.id}`);
      navigate(`/build/${data.id}`);
    } catch (e) {
      toast.error(apiErr(e, "Couldn't open the refiner"));
    }
  };

  // Buyer's "modify what I bought" lane (2026-07-13): fork a private copy, land in its editor.
  const onFork = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/skills/${skill.id}/fork`);
      toast.success(data.message || "Copied to My Artifacts");
      navigate(`/skill/${data.id}/edit`);
    } catch (e) {
      toast.error(apiErr(e, "Couldn't make a copy"));
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <AppShell>
        <div className="flex justify-center py-24 text-[color:var(--rb-text-faint)]"><Loader2 className="h-6 w-6 animate-spin" /></div>
      </AppShell>
    );
  }
  if (!skill) return null;

  const previewSkill = {
    slug: skill.slug,
    category: skill.category,
    short_description: skill.short_description,
    skill_md: skill.skill_md,
    files: skill.files || [],
  };

  // Only skill/agent have an AI builder to refine with (upload-only kinds don't).
  const canRefine = skill.is_creator && !["command", "output_style", "claude_md"].includes(skill.kind);
  const isCreator = skill.is_creator;
  const price = skill.price_cents ?? 100;

  return (
    <AppShell>
      <button onClick={() => navigate(back.to)} data-testid="detail-breadcrumb" className="mb-4 flex items-center gap-1.5 text-sm text-[color:var(--rb-text-faint)] hover:text-[color:var(--rb-text)]">
        <ArrowLeft className="h-4 w-4" /> {back.label}
      </button>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1.3fr_0.7fr]">
        {/* Left column */}
        <div className="min-w-0 space-y-6">
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <Badge variant="outline" className="border-[color:var(--rb-border)] text-[color:var(--rb-text-faint)]">{skill.category || "General"}</Badge>
              {kindBadge(skill.kind) && (
                <Badge data-testid="kind-badge" className="border-[color:rgba(94,234,212,0.3)] bg-[color:rgba(45,212,191,0.12)] text-[color:var(--rb-accent)]">{kindBadge(skill.kind)}</Badge>
              )}
              <StarRating rating={skill.avg_rating || 0} count={skill.review_count || 0} size={16} />
            </div>
            <h1 className="rb-mono mt-3 text-2xl font-semibold text-[color:var(--rb-text)]">{skill.slug}</h1>
            <p className="mt-2 text-sm leading-relaxed text-[color:var(--rb-text-dim)]">{skill.long_description || skill.short_description}</p>
          </div>

          <div className="h-[70vh]">
            <SkillPreviewPane skill={previewSkill} locked={!skill.unlocked} />
          </div>

          {skill.unlocked && <VersionHistory skillId={skill.id} canRestore={skill.is_creator} onRestored={load} />}

          <div className="rounded-2xl border border-[color:var(--rb-border)] bg-[color:var(--rb-obsidian)] p-5">
            <ReviewSection skillId={skill.id} onChanged={load} />
          </div>
        </div>

        {/* Right column - purchase card */}
        <div className="lg:sticky lg:top-24 lg:self-start">
          <div className="rounded-2xl border border-[color:rgba(212,175,55,0.28)] bg-[color:var(--rb-obsidian)] p-5 shadow-[0_0_0_1px_rgba(212,175,55,0.12),0_0_22px_rgba(212,175,55,0.06)]">
            {isCreator ? (
              <div data-testid="skill-price-creator">
                {skill.is_public ? (
                  <>
                    <div className="flex items-baseline gap-1">
                      <span className="rb-display text-3xl font-semibold text-[color:var(--rb-text)]">{price === 0 ? "Free" : creditsLabel(price)}</span>
                      <span className="text-sm text-[color:var(--rb-text-faint)]">{price === 0 ? "to everyone" : "charged to buyers"}</span>
                    </div>
                    <p className="mt-1 text-xs text-[color:var(--rb-text-faint)]">Live on the marketplace — you keep 80% of each sale.</p>
                  </>
                ) : (
                  <>
                    <div className="rb-display text-2xl font-semibold text-[color:var(--rb-text)]">Private draft</div>
                    <p className="mt-1 text-xs text-[color:var(--rb-text-faint)]">
                      {org
                        ? (skill.review_status === "approved"
                            ? "Only you can see this — it syncs to your machines via the Runebox plugin. Share it with your org from My Artifacts."
                            : "Only you can see this. Share it with your org from My Artifacts.")
                        : "Only you can see this. Publish it from My Artifacts to start charging buyers."}
                    </p>
                  </>
                )}
              </div>
            ) : (
              <>
                <div className="flex items-baseline gap-1" data-testid="skill-price">
                  <span className="rb-display text-3xl font-semibold text-[color:var(--rb-text)]">
                    {price === 0 ? "Free" : creditsLabel(price)}
                  </span>
                  <span className="text-sm text-[color:var(--rb-text-faint)]">{price === 0 ? "" : "one-time"}</span>
                </div>
                <p className="mt-1 text-xs text-[color:var(--rb-text-faint)]">{kindOf(skill.kind).lands ? `Downloads as a single file for ${kindOf(skill.kind).lands}` : `Includes SKILL.md + ${(skill.files_meta?.length || 0)} supporting file(s)`}</p>
              </>
            )}

            {skill.unlocked && !isCreator && org ? (
              // Org members don't download (2026-07-13): the plugin installs purchases.
              <div data-testid="plugin-sync-note" className="mt-4 flex items-center gap-2 rounded-xl border border-[color:rgba(45,212,191,0.3)] bg-[color:rgba(45,212,191,0.08)] p-3 text-sm text-[color:var(--rb-accent)]">
                <RefreshCw className="h-4 w-4 shrink-0" /> In your library — the Runebox plugin syncs it to your machine.
              </div>
            ) : skill.unlocked || isCreator ? (
              <Button onClick={onDownload} data-testid="download-button"
                className="mt-4 h-11 w-full rounded-xl bg-[color:var(--rb-accent)] text-[color:var(--rb-void)] hover:bg-[color:var(--rb-accent-2)]">
                <Download className="mr-2 h-4 w-4" /> Download .zip
              </Button>
            ) : (
              <Button onClick={onBuy} disabled={busy} data-testid="buy-download-button"
                className="mt-4 h-11 w-full rounded-xl bg-[color:var(--rb-accent)] text-[color:var(--rb-void)] hover:bg-[color:var(--rb-accent-2)]">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Lock className="mr-2 h-4 w-4" /> {(skill.price_cents ?? 100) === 0 ? "Get it free" : org ? `Buy · ${creditsLabel(skill.price_cents ?? 100)}` : `Buy & Download · ${creditsLabel(skill.price_cents ?? 100)}`}</>}
              </Button>
            )}

            {/* Skills upload to the Claude apps (web + desktop) as a folder-zip — shown wherever
                a Download would be (creator, or a solo unlocked buyer), skill kind only. */}
            {(skill.kind || "skill") === "skill" && (isCreator || (skill.unlocked && !org)) && (
              <UseInClaudeApps onDownload={onDownload} className="mt-2 h-11 w-full" />
            )}

            {skill.unlocked && !isCreator && (
              <Button onClick={onFork} disabled={busy} variant="outline" data-testid="fork-button"
                className="mt-2 h-11 w-full rounded-xl border-[color:var(--rb-border)] text-[color:var(--rb-text)] hover:border-[color:var(--rb-border-strong)] hover:bg-[color:var(--rb-surface)]">
                <CopyPlus className="mr-2 h-4 w-4 text-[color:var(--rb-accent)]" /> Make an editable copy
              </Button>
            )}

            {/* Every owned artifact is editable, however it was born — upload, fork, or the
                AI builder (2026-07-14). "Modify" because changed content re-enters review. */}
            {isCreator && (
              <Button onClick={() => navigate(`/skill/${skill.id}/edit`)} variant="outline" data-testid="edit-content-button"
                className="mt-2 h-11 w-full rounded-xl border-[color:var(--rb-border)] text-[color:var(--rb-text)] hover:border-[color:var(--rb-border-strong)] hover:bg-[color:var(--rb-surface)]">
                <PenLine className="mr-2 h-4 w-4 text-[color:var(--rb-accent)]" /> Modify
              </Button>
            )}

            {canRefine && (
              <Button onClick={onRefine} variant="outline" data-testid="refine-button"
                className="mt-2 h-11 w-full rounded-xl border-[color:var(--rb-border)] text-[color:var(--rb-text)] hover:border-[color:var(--rb-border-strong)] hover:bg-[color:var(--rb-surface)]">
                <Sparkles className="mr-2 h-4 w-4 text-[color:var(--rb-accent)]" /> Refine in builder
              </Button>
            )}

            {/* Org members neither sell publicly nor price anything (2026-07-14). */}
            {isCreator && !org && <PriceEditor skill={skill} onSaved={load} />}

            <div className="mt-4 flex items-center gap-2 border-t border-[color:var(--rb-border)] pt-4">
              <Avatar className="h-8 w-8">
                <AvatarImage src={skill.creator_avatar} />
                <AvatarFallback className="bg-[color:var(--rb-surface)] text-xs text-[color:var(--rb-accent)]">{(skill.creator_name || "U").slice(0, 1).toUpperCase()}</AvatarFallback>
              </Avatar>
              <div className="text-sm">
                <div className="text-[color:var(--rb-text)]">{skill.creator_name ? `@${skill.creator_name}` : "Anonymous"}</div>
                <div className="text-xs text-[color:var(--rb-text-faint)]">Creator</div>
              </div>
            </div>

            <div className="mt-4 grid grid-cols-2 gap-3 text-center">
              <div className="rounded-xl border border-[color:var(--rb-border)] bg-[color:var(--rb-surface)] p-3">
                <div className="text-lg font-semibold text-[color:var(--rb-text)]">{skill.download_count || 0}</div>
                <div className="text-xs text-[color:var(--rb-text-faint)]">Downloads</div>
              </div>
              <div className="rounded-xl border border-[color:var(--rb-border)] bg-[color:var(--rb-surface)] p-3">
                <div className="text-lg font-semibold text-[color:var(--rb-text)]">{(skill.avg_rating || 0).toFixed(1)}</div>
                <div className="text-xs text-[color:var(--rb-text-faint)]">Avg rating</div>
              </div>
            </div>

            <div className="mt-4 flex items-center gap-2 rounded-xl border border-[color:var(--rb-border)] bg-[color:var(--rb-surface)] p-3 text-xs text-[color:var(--rb-text-faint)]">
              <ShieldCheck className="h-4 w-4 text-[color:var(--rb-accent)]" /> Reviews are from verified buyers only.
            </div>
          </div>
        </div>
      </div>
    </AppShell>
  );
}
